import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  resource,
  Signal,
  untracked,
} from '@angular/core';
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop';
import {
  Einheiteneingabe,
  EinheiteneingabeEinheit,
  EinheiteneingabeService,
  unitEntryModalFn,
} from '@softapps/wws/core';
import { MengenEingabeComponent } from '@softline/application';
import { lastValueFrom, map, skip } from 'rxjs';
import { Artikel } from './../../types/artikel';
import { Einheit } from './../../types/einheit';
import { DefaultUnitEntryStrategy } from './strategies/default-unit-entry.strategy';

@Component({
  selector: 'soft-stock-quantity-control',
  imports: [CommonModule, MengenEingabeComponent],
  templateUrl: './quantity-control.html',
  styleUrl: './quantity-control.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuantityControl {
  readonly #unitEntryService = inject(EinheiteneingabeService);

  readonly product = input.required<Artikel>();
  readonly artehEingabe = input(true);
  readonly artehEingabeRequired = input(false);
  readonly unit = input<Einheit | null>();
  readonly quantity = input<number | null>(null);
  readonly unitEntryId = input<number | null>();

  readonly _unit = computed(() => this.unit());

  readonly strategy = computed(() => {
    return new DefaultUnitEntryStrategy();
  });

  readonly unitEntryModal = unitEntryModalFn();

  readonly unitEntryResource = resource({
    params: () => {
      const unit = this._unit();
      if (!unit || !this.artehEingabe()) return undefined;
      return {
        idartstamm: this.product().id,
        idarteheingabe: this.unitEntryId() ?? undefined,
        unit,
      };
    },
    loader: async ({ params }) => {
      if (!params) return undefined;
      const unitId = params.unit.id;
      const quantity = untracked(() => this.quantity() ?? 0);
      const initResult = await lastValueFrom(
        this.#unitEntryService.initialisierenOnly({
          ...params,
          menge: quantity,
          idbasisEinheit: unitId,
        })
      );

      const einheiten = [...initResult.einheiten];

      if (initResult && initResult.erfassungsEinheit?.menge) {
        const index = einheiten.findIndex(
          (e) => e.einheit.id === initResult.erfassungsEinheit.einheit.id
        );
        einheiten[index] = initResult.erfassungsEinheit;

        for (let i = 0; i < einheiten.length; i++) {
          if (einheiten[i].einheit.id === initResult?.erfassungsEinheit?.einheit.id) {
            einheiten[i].menge = initResult?.erfassungsEinheit?.menge ?? 0;
          } else {
            einheiten[i].menge = null;
          }
        }
      }

      const hasValue = initResult && !!initResult.erfassungsEinheit?.menge;
      const _quantity = hasValue ? initResult.erfassungsEinheit.menge : 1;
      const _unitId = hasValue ? initResult.erfassungsEinheit.einheit.id : unitId;

      return this.updateUnitEntry(initResult, _quantity, _unitId);
    },
  });

  private readonly _unitEntryId = linkedSignal(() => {
    const unitEntry = this.unitEntryResource.value();

    if (unitEntry) return unitEntry.idarteheingabe;

    return this.unitEntryId();
  });

  /**
   * The quantity and the unit in which the goods are stored (Lagereinheit)
   */
  readonly amount: Signal<{
    quantity: number;
    unit: Einheit | undefined;
    unitEntryId?: number | null;
    baseQuantity?: number | null;
  }> = linkedSignal(() => {
    const unitEntry = this.unitEntryResource.value();

    return {
      quantity: unitEntry?.summeEinheit?.menge ?? this.inputAmount()?.quantity ?? 0,
      unitEntryId: this._unitEntryId(),
      baseQuantity: !this.artehEingabe()
        ? this.inputAmount()?.quantity
        : (unitEntry?.erfassungsEinheit?.menge ?? this.inputAmount()?.quantity ?? null),
      unit: unitEntry?.summeEinheit?.einheit
        ? this.unitEntryUnitToLocalUnit(unitEntry?.summeEinheit?.einheit)
        : (this._unit() ?? undefined),
    };
  });

  /**
   * The quantity and the unit the user enters. (Erfassungseinheit)
   */
  readonly inputAmount = linkedSignal(() => {
    const unitEntry = this.unitEntryResource.value();

    if (!unitEntry) {
      return {
        quantity: this.quantity(),
        unit: this._unit(),
      };
    }

    return {
      quantity: unitEntry.erfassungsEinheit.menge,
      unit: {
        id: unitEntry.erfassungsEinheit.einheit.id,
        arteh: unitEntry.erfassungsEinheit.einheit.arteh,
        bezeichnung: unitEntry.erfassungsEinheit.einheit.bezeichnung,
        faktor: unitEntry.erfassungsEinheit.einheit.faktor,
        eannummer: null,
      } as Einheit,
    };
  });

  readonly quantityChange = outputFromObservable(
    toObservable(this.amount).pipe(
      map((amount) => ({
        quantity: amount.quantity,
        baseQuantity: amount?.baseQuantity,
        unitEntryId: amount?.unitEntryId,
      })),
      skip(1)
    )
  );

  readonly unitEntryEnabled = computed(() => {
    return this.artehEingabe() ?? false;
  });

  readonly quantityInputDisabled = computed(() => {
    const unitEntry = this.unitEntryResource.value();
    const required = this.artehEingabeRequired();

    if (!required) {
      return false;
    }

    const isWidthDimensionValid =
      unitEntry?.dimensionen?.breiteBez !== null
        ? !!unitEntry?.dimensionen?.breite && !isNaN(unitEntry?.dimensionen?.breite)
        : true;
    const isHeightDimensionValid =
      unitEntry?.dimensionen?.hoeheBez !== null
        ? !!unitEntry?.dimensionen?.hoehe && !isNaN(unitEntry?.dimensionen?.hoehe)
        : true;
    const isDepthDimensionValid =
      unitEntry?.dimensionen?.tiefeBez !== null
        ? !!unitEntry?.dimensionen?.tiefe && !isNaN(unitEntry?.dimensionen?.tiefe)
        : true;

    return !isWidthDimensionValid || !isHeightDimensionValid || !isDepthDimensionValid;
  });

  async onQuantityChange(value: number | null): Promise<void> {
    const unitEntry = this.unitEntryResource.value();
    const artehEingabe = this.artehEingabe();

    if (!!unitEntry && artehEingabe === true) {
      const unit = unitEntry.erfassungsEinheit.einheit;
      try {
        const result = await this.updateUnitEntry(unitEntry, value, unit.id);
        this.unitEntryResource.set(result);
      } catch (e) {
        console.log(e);
      }
    } else {
      this.inputAmount.set({
        ...this.inputAmount(),
        quantity: value,
      });
    }
  }

  async openUnitEntryModal(): Promise<void> {
    const unit = this._unit();
    if (!unit) return;
    const result = await this.unitEntryModal({
      params: {
        idartstamm: this.product().id,
        menge: this.quantity() ?? 1,
        idarteheingabe: this._unitEntryId() ?? undefined,
        idbasisEinheit: unit.id,
      },
    });

    if (!result || result === 'DISMISSED') return;

    this.unitEntryResource.set(result);
  }

  private async updateUnitEntry(
    unitEntry: Einheiteneingabe,
    value: number | null,
    unitId: number
  ): Promise<Einheiteneingabe> {
    const units = [...unitEntry.einheiten];
    const unitIndex = units.findIndex((u) => u.einheit.id === unitId);

    // reset all units to null and only set the quantity of the inventory unit
    units.forEach((unit, index) => {
      units[index] = {
        ...unit,
        menge: index === unitIndex ? (value ?? 0) : null,
      };
    });

    return await lastValueFrom(
      this.#unitEntryService.umrechnen({
        ...unitEntry,
        einheiten: units,
        erfassungsEinheit: {
          ...unitEntry.erfassungsEinheit,
          menge: null,
        },
      })
    );
  }

  private unitEntryUnitToLocalUnit(value: EinheiteneingabeEinheit): Einheit {
    return {
      id: value.id,
      arteh: value.arteh,
    };
  }
}
